import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import '../restaurant/css/Restaurants.css';
import '../restaurant/css/OrderDetails.css';
import {
  extractOrganizationName,
  fetchOrganizationById,
  fetchOrganizationRootAdmin,
  updateOrganization,
  updateOrganizationUser,
  deleteOrganization,
} from './organizationApi';
import {
  formatMobileDisplayOrFallback,
  normalizeAdminEntityStatus,
  isInactiveEntityStatus,
} from '../restaurant/formatDisplay';
import { confirmAction } from '../restaurant/ui/feedback';
import EatsAdminPageHeader from '../admin/EatsAdminPageHeader';
import OwnerDetailsSection from '../restaurant/OwnerDetailsSection';

const ADMIN_ORGANIZATIONS_PATH = '/eats/admin?tab=organizations';

const OrganizationDetail = ({ organizationId: organizationIdProp, embeddedInAdmin = false }) => {
  const { organizationId: routeOrganizationId } = useParams();
  const organizationId = organizationIdProp ?? routeOrganizationId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!organization || searchParams.get('edit') !== '1') return;
    setIsEditing(true);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const [organizationName, setOrganizationName] = useState('');
  const [organizationCode, setOrganizationCode] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [admissions, setAdmissions] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [offers, setOffers] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerReloadToken, setOwnerReloadToken] = useState(0);

  const applyOrganizationToForm = useCallback((data) => {
    if (!data) return;
    setOrganizationName(data.organizationName ?? extractOrganizationName(data));
    setOrganizationCode(data.organizationCode ?? '');
    setOrganizationType(data.organizationType ?? '');
    setPhoneNumber(data.phoneNumber != null ? String(data.phoneNumber) : '');
    setEmail(data.email ?? '');
    setWebsite(data.website ?? '');
    setAdmissions(data.admissions ?? '');
    setSpecialization(data.specialization ?? '');
    setOffers(data.offers ?? '');
    setNotes(data.notes ?? '');
    setStatus(normalizeAdminEntityStatus(data.status ?? ''));
  }, []);

  const loadOrganization = useCallback(async () => {
    const id = organizationId != null ? String(organizationId).trim() : '';
    if (!id || id === 'null' || id === 'undefined') {
      setError('Invalid organization id.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await fetchOrganizationById(id);
      setOrganization(data);
      applyOrganizationToForm(data);
    } catch (e) {
      console.error('Organization detail fetch failed:', e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to load organization details.'
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, applyOrganizationToForm]);

  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  useEffect(() => {
    if (!embeddedInAdmin || !organizationId) {
      setOwnerDetails(null);
      setOwnerLoading(false);
      return;
    }

    const id = String(organizationId).trim();
    if (!id || id === 'null' || id === 'undefined') {
      setOwnerDetails(null);
      return;
    }

    let cancelled = false;
    setOwnerLoading(true);
    fetchOrganizationRootAdmin(id)
      .then((owner) => {
        if (!cancelled) setOwnerDetails(owner);
      })
      .catch((error) => {
        console.warn('Organization owner fetch failed:', error);
        if (!cancelled) setOwnerDetails(null);
      })
      .finally(() => {
        if (!cancelled) setOwnerLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [embeddedInAdmin, organizationId, ownerReloadToken]);

  const handleSaveOwner = async (payload) => {
    if (!ownerDetails?.id) {
      throw new Error('Owner is not assigned.');
    }
    await updateOrganizationUser(ownerDetails.id, {
      firstName: payload.firstName,
      lastName: payload.lastName,
      name: payload.name,
      email: payload.email,
      mobile: payload.mobile,
      contactNumber: payload.contactNumber,
      aadharNo: payload.aadharNo,
      organizationId,
      updatedById: user?.userId ?? 1,
    });
    setOwnerReloadToken((token) => token + 1);
    window.alert('Owner updated successfully');
  };

  const handleUpdate = async () => {
    if (!organizationId) return;

    const phoneDigits = String(phoneNumber || '').replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      window.alert('Please enter a valid 10-digit phone number.');
      return;
    }

    setSaving(true);
    try {
      const result = await updateOrganization(
        organizationId,
        {
          organizationName,
          organizationCode,
          organizationType,
          phoneNumber,
          email,
          website,
          admissions,
          specialization,
          offers,
          notes,
          status,
        },
        user?.userId ?? 1
      );

      alert(result?.message || 'Organization updated successfully');
      setIsEditing(false);
      await loadOrganization();
    } catch (e) {
      console.error('Update organization error:', e);
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error_message ||
          'Failed to update organization'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!organizationId) return;

    const ok = await confirmAction({
      title: 'Delete organization?',
      message: 'Are you sure you want to delete this organization? This action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;

    try {
      const result = await deleteOrganization(organizationId);
      alert(result?.message || 'Organization deactivated successfully');

      if (String(localStorage.getItem('organizationId')) === String(organizationId)) {
        localStorage.removeItem('organizationId');
      }
      navigate(embeddedInAdmin ? ADMIN_ORGANIZATIONS_PATH : '/organization/admin');
    } catch (e) {
      console.error('Delete organization error:', e);
      alert(e?.response?.data?.message || 'Failed to deactivate organization');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (organization) applyOrganizationToForm(organization);
  };

  const displayName =
    extractOrganizationName(organization) || organizationName || 'Organization details';

  const displayValue = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
  };

  const DetailField = ({ label, value, wide = false }) => (
    <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
      <span className="restaurant-detail-label">{label}</span>
      <span className="restaurant-detail-value">{displayValue(value)}</span>
    </div>
  );

  const handleBack = () => {
    if (embeddedInAdmin) {
      navigate(ADMIN_ORGANIZATIONS_PATH);
      return;
    }
    navigate(-1);
  };

  const addresses = Array.isArray(organization?.addresses) ? organization.addresses : [];

  return (
    <div
      className={`order-details restaurant-detail-page${
        embeddedInAdmin ? ' restaurant-detail-page-admin' : ''
      }`}
    >
      <div className="restaurant-detail-panel">
        <EatsAdminPageHeader
          title={displayName}
          homeTo={embeddedInAdmin ? '/eats/admin' : '/home'}
          onBack={handleBack}
          actions={
            organization && !loading && !isEditing && !isInactiveEntityStatus(status) ? (
              <button type="button" className="eats-admin-header-btn" onClick={handleDelete}>
                Delete
              </button>
            ) : null
          }
        />

        {loading && <p className="restaurant-detail-message">Loading…</p>}
        {!loading && error && <p className="restaurant-detail-message error">{error}</p>}

        {!loading && !error && organization && (
          <>
            <div className="restaurant-detail-card">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Organization Information</h3>
                <div className="restaurant-detail-card-header-actions">
                  {!isEditing && status ? (
                    <span
                      className={`restaurant-detail-badge restaurant-detail-badge-${String(status)
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`}
                    >
                      {status}
                    </span>
                  ) : null}
                  {!isEditing ? (
                    <button
                      type="button"
                      className="eats-admin-header-btn"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="eats-admin-header-btn"
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="eats-admin-header-btn"
                        onClick={handleUpdate}
                        disabled={saving}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="restaurant-detail-edit-form">
                  <div className="get-restaurant-form-row flex">
                    <div className="get-restaurant-form-group">
                      <label>Organization Name</label>
                      <input
                        type="text"
                        value={organizationName}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        readOnly={!embeddedInAdmin}
                        disabled={!embeddedInAdmin}
                        className={embeddedInAdmin ? undefined : 'restaurant-detail-field-locked'}
                      />
                    </div>
                    <div className="get-restaurant-form-group">
                      <label>Organization Code</label>
                      <input
                        type="text"
                        value={organizationCode}
                        onChange={(e) => setOrganizationCode(e.target.value)}
                        readOnly={!embeddedInAdmin}
                        disabled={!embeddedInAdmin}
                        className={embeddedInAdmin ? undefined : 'restaurant-detail-field-locked'}
                      />
                    </div>
                    <div className="get-restaurant-form-group">
                      <label>Organization Type</label>
                      <input
                        type="text"
                        value={organizationType}
                        onChange={(e) => setOrganizationType(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="get-restaurant-form-row flex">
                    <div className="get-restaurant-form-group">
                      <label>Phone Number</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      />
                    </div>
                    <div className="get-restaurant-form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="get-restaurant-form-group">
                      <label>Website</label>
                      <input
                        type="text"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="get-restaurant-form-row flex">
                    <div className="get-restaurant-form-group">
                      <label>Admissions</label>
                      <input
                        type="text"
                        value={admissions}
                        onChange={(e) => setAdmissions(e.target.value)}
                      />
                    </div>
                    <div className="get-restaurant-form-group">
                      <label>Specialization</label>
                      <input
                        type="text"
                        value={specialization}
                        onChange={(e) => setSpecialization(e.target.value)}
                      />
                    </div>
                    <div className="get-restaurant-form-group">
                      <label>Offers</label>
                      <input
                        type="text"
                        value={offers}
                        onChange={(e) => setOffers(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="get-restaurant-form-row flex">
                    <div className="get-restaurant-form-group">
                      <label>Status</label>
                      {embeddedInAdmin ? (
                        <select value={normalizeAdminEntityStatus(status)} onChange={(e) => setStatus(e.target.value)}>
                          <option value="ACTIVE">Active</option>
                          <option value="INACTIVE">Inactive</option>
                        </select>
                      ) : (
                        <input type="text" value={status} readOnly />
                      )}
                    </div>
                    <div className="get-restaurant-form-group get-restaurant-form-group-wide">
                      <label>Notes</label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="restaurant-detail-grid">
                  <DetailField label="Organization Name" value={organizationName} />
                  <DetailField label="Organization Code" value={organizationCode} />
                  <DetailField label="Organization Type" value={organizationType} />
                  <DetailField label="Phone Number" value={formatMobileDisplayOrFallback(phoneNumber, '—')} />
                  <DetailField label="Email" value={email} />
                  <DetailField label="Website" value={website} />
                  <DetailField label="Admissions" value={admissions} />
                  <DetailField label="Specialization" value={specialization} />
                  <DetailField label="Offers" value={offers} />
                  <DetailField label="Status" value={status} />
                  <DetailField label="Notes" value={notes} wide />
                </div>
              )}
            </div>

            {embeddedInAdmin ? (
              <OwnerDetailsSection
                title="Owner Details"
                owner={ownerDetails}
                loading={ownerLoading}
                canEdit
                onSave={handleSaveOwner}
                emptyBody={<p className="restaurant-detail-message">No owner assigned yet.</p>}
              />
            ) : null}

            <div className="restaurant-detail-card restaurant-detail-address-card">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Address</h3>
              </div>
              <div className="restaurant-detail-address-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Address Line 1</th>
                      <th>Address Line 2</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Country</th>
                      <th>Postal Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addresses.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="restaurant-detail-empty">
                          No addresses available.
                        </td>
                      </tr>
                    ) : (
                      addresses.map((addr, index) => (
                        <tr key={addr.addressId ?? index}>
                          <td>{displayValue(addr.addressLine1)}</td>
                          <td>{displayValue(addr.addressLine2)}</td>
                          <td>{displayValue(addr.city)}</td>
                          <td>{displayValue(addr.state)}</td>
                          <td>{displayValue(addr.country)}</td>
                          <td>{displayValue(addr.postalCode)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrganizationDetail;
